import { useCallback } from 'react';

import { FormValues } from '../organisms';

import { FormikHelpers } from 'formik';

type Params = {
  setLists: React.Dispatch<React.SetStateAction<any>>;
  setFieldValue: FormikHelpers<FormValues>['setFieldValue'];
  values: FormValues;
};

/**
 * Hook to handle logic after successfully submitting a new address
 * for any type of person (beneficiary, payer, owner, insured person)
 */
export const useHandleAddressDrawerSuccess = ({
  setLists,
  setFieldValue,
  values
}: Params) => {
  const handleAddressDrawerSubmit = useCallback(
    async (
      addrId: string,
      addrText: string,
      personId: string,
      idxToUpdate?: number,
      personType?: 'beneficiary' | 'payer' | 'owner' | 'insured'
    ) => {
      // ------------------------ PAYER ------------------------
      if (personId === values.payerOwner?.payer?.id || personType === 'payer') {
        setLists((prev: any) => ({
          ...prev,
          payerAddresses: { ...prev.payerAddresses, [addrId]: addrText }
        }));
        await setFieldValue(`payerOwner.payer.address`, addrId || '', false);
        await setFieldValue(
          `payerOwner.payer.addressDetails`,
          addrText || '',
          false
        );
      }

      // ------------------------ OWNER ------------------------
      if (personId === values.payerOwner?.owner?.id || personType === 'owner') {
        setLists((prev: any) => ({
          ...prev,
          ownerAddresses: { ...prev.ownerAddresses, [addrId]: addrText }
        }));
        await setFieldValue(
          `payerOwner.owner.ownerAddress`,
          addrId || '',
          false
        );
        await setFieldValue(
          `payerOwner.owner.addressDetails`,
          addrText || '',
          false
        );
      }

      // ------------------------ INSURED ------------------------
      for (let index = 0; index < values.insuredPerson.length; index++) {
        const insured = values.insuredPerson[index];
        if (personId === insured?.id || personType === 'insured') {
          const indexToUpdateFinal =
            personType === 'insured' && idxToUpdate !== undefined
              ? idxToUpdate
              : index;

          setLists((prev: any) => ({
            ...prev,
            insuredAddresses: { ...prev.insuredAddresses, [addrId]: addrText }
          }));

          if (personId === insured?.id) {
            await setFieldValue(
              `insuredPerson.${indexToUpdateFinal}.address`,
              addrId || '',
              false
            );
            await setFieldValue(
              `insuredPerson.${indexToUpdateFinal}.addressDetails`,
              addrText || '',
              false
            );
          }
        }
      }

      // ------------------------ MEDICAL INSURED ------------------------
      for (let index = 0; index < values.medicalInsureds.length; index++) {
        const medicalInsured = values.medicalInsureds[index];
        if (
          personId === medicalInsured?.insuredID ||
          personType === 'insured'
        ) {
          const indexToUpdateFinal =
            personType === 'insured' && idxToUpdate !== undefined
              ? idxToUpdate
              : index;

          setLists((prev: any) => ({
            ...prev,
            insuredAddresses: { ...prev.insuredAddresses, [addrId]: addrText }
          }));

          if (personId === medicalInsured?.insuredID) {
            await setFieldValue(
              `medicalInsureds.${indexToUpdateFinal}.addressID`,
              addrId || '',
              false
            );
          }
        }
      }

      // ------------------------ BENEFICIARIES ------------------------
      if (values.beneficiaries?.length) {
        for (let index = 0; index < values.beneficiaries.length; index++) {
          const bene = values.beneficiaries[index];
          if (personId === bene?.id || personType === 'beneficiary') {
            const idxFinal =
              personType === 'beneficiary' && idxToUpdate !== undefined
                ? idxToUpdate
                : index;

            setLists((prev: any) => ({
              ...prev,
              beneAddresses: {
                ...prev.beneAddresses,
                [idxFinal]: { [addrId]: addrText }
              }
            }));

            await setFieldValue(
              `beneficiaries.${idxFinal}.address`,
              addrId || '',
              false
            );
            await setFieldValue(
              `beneficiaries.${idxFinal}.addressDetails`,
              addrText || '',
              false
            );
          }
        }
      }
    },
    [setLists, setFieldValue, values]
  );

  return { handleAddressDrawerSubmit };
};
